'use strict';
import isFunction from 'lodash/isFunction';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import isUndefined from 'lodash/isUndefined';
import noop from 'lodash/noop';
import {ReplaySubject} from 'rxjs/ReplaySubject';
import {Observable} from 'rxjs/Observable';
import 'rxjs/add/observable/fromPromise';
import {SCOPE_DESTROY} from '../../config/constants/view-events';
import {eventToObservable} from '../../core-utilities/rx-utils';
import {BUTTON_STATES} from './CONFIRM_BUTTON_STATES';
import template from './confirm-button.html';

ConfirmButtonDirective.$inject = [];

ConfirmButtonController.$inject = [
  '$scope',
  '$element',
  '$attrs',
  '$timeout',
  '$parse',
  '$translate',
];

/**
 * Attributes
 * Currently, attributes only support fixed string inputs
 *
 *
 * Attributes required:
 * start-label
 * on-confirm($event, $state)
 *
 * Attributes supported:
 * start-style      = confirm-button-start
 * on-start         = noop($event, $state) - called when the button is pressed in start state, which automatically transitions to confirm state
 * confirm-label    = confirm.button.confirm.label
 * confirm-style    = confirm-button-confirm
 * loading-label    = confirm.button.loading.label
 * loading-style    = confirm-button-loading
 * success-label    = confirm.button.success.label
 * success-style    = confirm-button-success
 * on-success       = noop($event, $state) - called after the timeout expires or the button is pressed, while in the success state
 * failure-label    = confirm.button.failure.label
 * failure-style    = confirm-button-failure
 * on-failure       = noop($event, $state) - called after the timeout expires or the button is pressed, while in the failure state
 * complete-timeout = 750 - the time to show the success/failure state. if falsey, state will persist until clicked
 */
export function ConfirmButtonDirective() {
  return {
    restrict: 'E',
    template: template,
    scope: {
      api: '=?',
      working: '=?'
    },
    controllerAs: 'confirmButton',
    bindToController: true,
    controller: ConfirmButtonController
  };
}

function ConfirmButtonController($scope, $element, $attrs, $timeout, $parse, $translate) {
  const vm = this;
  let timeout = !isUndefined($attrs.completeTimeout) && !$attrs.completeTimeout ? 0 : (isNumber($attrs.completeTimeout) ? $attrs.completeTimeout : 750);
  if (!$attrs.startLabel) {
    console.warn('ConfirmButtonController failed to parse method to call. Expected expressions for on-start an on-confirm. ' + $element);
    console.error(new Error('ConfirmButtonDirective expected string for start-label. ' + $element));
    return;
  }

  vm.getState = getState;
  vm.setState = setState;
  vm.isLoading = isLoading;

  vm.api = {
    getState: getState,
    setState: setState,
    onSuccess: onSuccess,
    onFailure: onFailure
  };

  vm.working = false;

  $element.on('click', _onClick);

  $scope.$on('$destroy', _cleanup);

  const state$ = new ReplaySubject(1);
  state$.next(BUTTON_STATES.START);

  const states$ = Observable.fromPromise(_initStates($attrs)
    .catch(err => {
      console.warn('ConfirmButtonController failed to parse method to call. Expected expressions for on-start an on-confirm. ' + $element);
      console.error(err);
      return null;
    }));

  Observable
    .combineLatest(
      state$,
      states$,
    )
    .map(([state, states]) => _updateState(state, states))
    .takeUntil(eventToObservable($scope, SCOPE_DESTROY))
    .subscribe(nextState => $scope.$evalAsync(() => Object.assign(vm, nextState)));

  function getState() {
    return vm.state;
  }

  function setState(state) {
    state$.next(state);
  }

  function isLoading() {
    return vm.state === BUTTON_STATES.LOADING;
  }

  function _updateState(state, states) {
    if (state !== BUTTON_STATES.START) {
      return {
        state,
        states,
        isFlipped: true,
        frontLabel: states[BUTTON_STATES.START].label,
        frontStyle: states[BUTTON_STATES.START].style,
        backLabel: states[state].label,
        backStyle: states[state].style,
      }
    } else {
      return {
        state,
        states,
        isFlipped: false,
        frontLabel: states[BUTTON_STATES.START].label,
        frontStyle: states[BUTTON_STATES.START].style,
        backLabel: states[BUTTON_STATES.CONFIRM].label,
        backStyle: states[BUTTON_STATES.CONFIRM].style,
      }
    }
  }

  function _onClick(e) {
    switch (vm.state) {
      case BUTTON_STATES.START:
        if (!vm.states[BUTTON_STATES.START].handler) {
          vm.working = true;
          setState(BUTTON_STATES.CONFIRM);
        } else {
          $scope.$apply(() => {
            vm.states[BUTTON_STATES.START].handler($scope.$parent, {$event: e, $state: BUTTON_STATES.START});

            setState(BUTTON_STATES.CONFIRM);
          });
        }
        $scope.$apply(() => {
          vm.states[BUTTON_STATES.START].handler($scope.$parent, {$event: e});

          setState(BUTTON_STATES.CONFIRM);
        });
        break;

      case BUTTON_STATES.CONFIRM:
        $scope.$apply(() => {
          const result = vm.states[BUTTON_STATES.CONFIRM].handler($scope.$parent, {
            $event: e,
            $state: BUTTON_STATES.CONFIRM
          });

          setState(BUTTON_STATES.LOADING);

          if (isObject(result) && isFunction(result.then)) {
            result.then(onSuccess, onFailure).finally(onFinally);
          } // else, the user must call
        });
        break;

      case BUTTON_STATES.SUCCESS:
        if (!vm.states[BUTTON_STATES.SUCCESS].handler) {
          setState(BUTTON_STATES.START);
        } else {
          $scope.$apply(() => {
            vm.states[BUTTON_STATES.SUCCESS].handler($scope.$parent, {
              $event: e,
              $state: BUTTON_STATES.SUCCESS
            });

            setState(BUTTON_STATES.START);
          });
        }
        break;

      case BUTTON_STATES.FAILURE:
        if (!vm.states[BUTTON_STATES.FAILURE].handler) {
          setState(BUTTON_STATES.START);
        } else {
          $scope.$apply(() => {
            vm.states[BUTTON_STATES.FAILURE].handler($scope.$parent, {
              $event: e,
              $state: BUTTON_STATES.FAILURE
            });

            setState(BUTTON_STATES.START);
          });
        }
        break;
    }
  }

  function onSuccess() {
    setState(BUTTON_STATES.SUCCESS);

    if (!vm.states[BUTTON_STATES.SUCCESS].handler) {
      if (timeout) $timeout(setState.bind(vm, BUTTON_STATES.START), timeout);
    } else if (timeout) {
      $timeout(() => {
        $scope.$apply(() => {
          vm.states[BUTTON_STATES.SUCCESS].handler($scope.$parent, {$state: BUTTON_STATES.SUCCESS});
          setState(BUTTON_STATES.START);
        });
      }, timeout);
    }
  }

  function onFailure() {
    setState(BUTTON_STATES.FAILURE);

    if (!vm.states[BUTTON_STATES.FAILURE].handler) {
      if (timeout) $timeout(setState.bind(vm, BUTTON_STATES.START), timeout);
    } else if (timeout) {
      $timeout(() => {
        $scope.$apply(() => {
          vm.states[BUTTON_STATES.FAILURE].handler($scope.$parent, {$state: BUTTON_STATES.FAILURE});
          setState(BUTTON_STATES.START);
        });
      }, timeout);
    }
  }

  function onFinally() {
    vm.working = false;
  }

  function _initStates(props) {
    const promises = [];
    const labels = [
      props.startLabel,
      props.confirmLabel || 'confirm.button.confirm.label',
      props.loadingLabel || 'confirm.button.loading.label',
      props.successLabel || 'confirm.button.success.label',
      props.failureLabel || 'confirm.button.failure.label'
    ];

    const states = [
      {
        style: props.startStyle || 'confirm-button-start',
        handler: isString(props.onStart) ? $parse(props.onStart) : noop
      },
      {
        style: props.confirmStyle || 'confirm-button-confirm',
        handler: $parse(props.onConfirm)
      },
      {
        style: isString(props.loadingStyle) ? props.loadingStyle + ' icon' : 'icon confirm-button-loading'
      },
      {
        style: props.successStyle || 'confirm-button-success',
        handler: isString(props.onSuccess) ? $parse(props.onSuccess) : noop
      },
      {
        style: props.failureStyle || 'confirm-button-failure',
        handler: isString(props.onfailure) ? $parse(props.onfailure) : noop
      }
    ];

    labels.forEach((msg, labelId) => {
      promises.push($translate(msg).then(label => {
        states[labelId].label = label;
      }));
    });

    return Promise.all(promises).then(() => states);
  }

  function _cleanup() {
    $element.off('click', _onClick);
  }
}



// WEBPACK FOOTER //
// ./src/js/core-components/transforming-buttons/ConfirmButtonDirective.js